import { AionCta } from "@/components/site/AionCta";
import { OG_IMAGES } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Headphones, Youtube } from "lucide-react";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { RelatedKeywords } from "@/components/site/RelatedKeywords";
import { RelatedPages } from "@/components/site/RelatedPages";
import { episodes } from "@/lib/podcast-episodes";

const TITLE = "Raj Shamani Podcast — Figuring Out: Episodes, Guests & Where to Listen";
const DESC =
  "Everything about the Raj Shamani podcast: what Figuring Out is, its biggest guests, episode list, release schedule, and where to listen on YouTube, Spotify and Apple Podcasts.";
const URL = "https://rajshamani.life/raj-shamani-podcast";
const OG = OG_IMAGES.podcast;

const faq = [
  {
    q: "What is the name of Raj Shamani's podcast?",
    a: "Raj Shamani hosts Figuring Out, a long-form interview podcast where he speaks with founders, investors, actors, athletes and thinkers about money, business and life.",
  },
  {
    q: "Where can I listen to the Raj Shamani podcast?",
    a: "Full video episodes are on YouTube, and audio versions are available on Spotify and Apple Podcasts. Every episode page on this site links to both.",
  },
  {
    q: "Who are the biggest guests on the Raj Shamani podcast?",
    a: "Guests have included Bill Gates, MS Dhoni, Shahid Kapoor, Kiara Advani, Zakir Khan, Ankur Warikoo, Khan Sir and dozens of India's leading founders and investors.",
  },
  {
    q: "How often does the Raj Shamani podcast release new episodes?",
    a: "Figuring Out publishes new long-form episodes on a weekly cadence, with shorter clips released between full episodes.",
  },
  {
    q: "How long is a typical Figuring Out episode?",
    a: "Most episodes run between 45 minutes and 2 hours, since the format is intentionally long-form and unedited in substance.",
  },
  {
    q: "How can I be a guest on the Raj Shamani podcast?",
    a: "Guest pitches go through the Be a Guest form on this site. Every submission is read by the Figuring Out production team.",
  },
];

export const Route = createFileRoute("/raj-shamani-podcast")({
  head: () => ({
    meta: [
      { title: TITLE, key: "title" },
      { name: "description", content: DESC, key: "description" },
      { property: "og:title", content: TITLE, key: "og:title" },
      { property: "og:description", content: DESC, key: "og:description" },
      { property: "og:url", content: URL, key: "og:url" },
      { property: "og:type", content: "website", key: "og:type" },
      { property: "og:image", content: OG, key: "og:image" },
      { property: "og:image:alt", content: "Raj Shamani hosting the Figuring Out podcast", key: "og:image:alt" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      { name: "twitter:title", content: TITLE, key: "twitter:title" },
      { name: "twitter:description", content: DESC, key: "twitter:description" },
      { name: "twitter:image", content: OG, key: "twitter:image" },
      { property: "og:locale", content: "en_IN", key: "og:locale" },
      { name: "twitter:site", content: "@rajshamani", key: "twitter:site" },
      { name: "twitter:creator", content: "@rajshamani", key: "twitter:creator" },
      { name: "geo.region", content: "IN", key: "geo.region" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${URL}#webpage`,
          name: TITLE,
          headline: TITLE,
          description: DESC,
          url: URL,
          inLanguage: "en",
          isPartOf: { "@type": "WebSite", "@id": "https://rajshamani.life/#website" },
          breadcrumb: { "@id": `${URL}#breadcrumb` },
          publisher: { "@type": "Organization", "@id": "https://rajshamani.life/#organization" },
          about: { "@type": "Person", name: "Raj Shamani", url: "https://rajshamani.life/about" },
          primaryImageOfPage: { "@type": "ImageObject", url: OG },
          image: OG,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          mainEntity: faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: PodcastLanding,
});

function PodcastLanding() {
  const latest = episodes.slice(0, 8);
  return (
    <div>
      <PageBreadcrumb current="Raj Shamani Podcast" />

      <section className="bg-noir border-b border-border/50">
        <div className="mx-auto max-w-5xl px-6 py-24">
          <div className="text-xs tracking-widest uppercase text-gold mb-4 inline-flex items-center gap-2">
            <Headphones size={14} /> The Podcast
          </div>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight">
            Raj Shamani Podcast — <span className="text-gradient-gold">Figuring Out</span>
          </h1>
          <p className="mt-8 text-lg text-muted-foreground max-w-3xl leading-relaxed">
            The Raj Shamani podcast is called <strong>Figuring Out</strong> — one of
            India's most-watched long-form interview shows. Every week Raj sits
            down with a founder, investor, actor, athlete or thinker for an
            unhurried conversation about money, business, craft and the parts of
            success nobody puts on a slide. This page covers what the show is,
            who has been on it, how often it releases, and exactly where to
            listen.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/podcast"
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-sm bg-gold text-primary-foreground font-medium hover:opacity-90 transition"
            >
              Browse all episodes
              <ArrowRight size={16} className="group-hover:translate-x-1 transition" />
            </Link>
            <a
              href="https://www.youtube.com/@rajshamani"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-sm border border-border text-foreground hover:border-gold/60 hover:text-gold transition"
            >
              <Youtube size={16} /> Watch on YouTube
            </a>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-10">Recent episodes</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {latest.map((e) => (
            <Link
              key={e.slug}
              to="/podcast/$slug"
              params={{ slug: e.slug }}
              className="block p-6 rounded-xl bg-card border border-border hover:border-gold/40 transition"
            >
              <div className="text-xs uppercase tracking-widest text-gold mb-2">
                {e.n} · {e.d}
              </div>
              <h3 className="font-bold mb-1">{e.g}</h3>
              <p className="text-sm text-muted-foreground">{e.t}</p>
            </Link>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-6 pb-20 space-y-12">
        <div>
          <h2 className="text-3xl md:text-4xl font-bold mb-5">
            What is the Raj Shamani podcast?
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            The Raj Shamani podcast is <strong>Figuring Out</strong>, launched in
            2020 as a way to ask the questions that never get asked on stage.
            The format is deliberately unhurried: one guest, one long
            conversation, no panel, no PR-approved script. Raj records in both
            English and Hindi depending on the guest, which is a large part of
            why the show reaches audiences well outside metro India.
          </p>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            The through-line across hundreds of episodes is money and craft —
            how people actually earned what they have, what it cost them, and
            what they would do differently. A founder gets asked about unit
            economics and burn, not vision. An actor gets asked about fees,
            rejection and career management. A cricketer gets asked about
            pressure and money decisions after retirement. That consistency of
            angle is why episodes travel far beyond the guest's own fanbase.
          </p>
        </div>

        <div>
          <h2 className="text-3xl md:text-4xl font-bold mb-5">
            Biggest guests on Figuring Out
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            The guest list spans four worlds. From global leadership and
            technology there have been conversations with figures such as{" "}
            <strong>Bill Gates</strong> and heads of state. From Indian business:
            founders and investors who built companies through the last decade
            of consumer growth — the Shark Tank cohort, D2C operators,
            public-market investors and the people quietly running category
            leaders nobody has heard of.
          </p>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            From entertainment and sport: <strong>MS Dhoni</strong>,{" "}
            <strong>Shahid Kapoor</strong>, <strong>Kiara Advani</strong> and{" "}
            <strong>Zakir Khan</strong>, all approached as businesses rather than
            as celebrities. And from the creator and education economy:{" "}
            <strong>Ankur Warikoo</strong>, <strong>Khan Sir</strong> and others
            who built audiences before they built revenue. Every episode on this
            site has its own page with a written summary and transcript
            highlights, so you can read the argument before committing two
            hours.
          </p>
        </div>

        <div>
          <h2 className="text-3xl md:text-4xl font-bold mb-5">
            Where to listen — YouTube, Spotify and Apple Podcasts
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            Full video episodes go up on YouTube, which is where the majority of
            the audience watches; the visual format matters because a lot of the
            signal in these conversations is in hesitation and body language.
            Audio-only versions publish simultaneously on Spotify and Apple
            Podcasts for commutes and gym listening. Shorter clips — the two- to
            five-minute segments that circulate on Instagram and YouTube Shorts
            — are cut from the same recordings and are the usual entry point for
            new listeners.
          </p>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            If you are starting from zero, the practical route is to open the{" "}
            <Link to="/podcast" className="text-gold hover:underline">
              full episode archive
            </Link>
            , filter by the topic you care about — money, D2C, careers, media —
            and read the summary before picking a full episode.
          </p>
        </div>

        <div>
          <h2 className="text-3xl md:text-4xl font-bold mb-5">
            Release schedule and episode length
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            Figuring Out publishes on a weekly cadence, with additional bonus
            conversations when a guest's schedule allows. Full episodes usually
            run between 45 minutes and two hours. Nothing is trimmed for pace:
            the editorial rule is that a slow answer that reveals something is
            worth more than a tight answer that reveals nothing, which is the
            opposite of how most Indian business media is produced.
          </p>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            Between full episodes, the channel releases clips and short-form
            segments. Those are best treated as trailers — the actual value sits
            in the unedited middle of a long conversation, roughly forty minutes
            in, when the guest stops performing.
          </p>
        </div>

        <div>
          <h2 className="text-3xl md:text-4xl font-bold mb-5">
            How to be a guest on the Raj Shamani podcast
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            Pitches go through the{" "}
            <Link to="/guest" className="text-gold hover:underline">
              Be a Guest form
            </Link>
            , and every submission is read by the production team. The strongest
            pitches are specific: a number you can defend, a decision you got
            wrong, an industry detail the audience has never heard explained
            properly. Follower count matters far less than whether you can say
            something true on record that nobody else can say.
          </p>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            For brand partnerships, sponsorships and speaking enquiries rather
            than guest pitches, use the{" "}
            <Link to="/contact" className="text-gold hover:underline">
              contact page
            </Link>{" "}
            instead — those go to a different inbox and get a faster answer.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">

        <h2 className="text-3xl md:text-4xl font-bold mb-10">
          Raj Shamani podcast — frequently asked questions
        </h2>
        <div className="space-y-8">
          {faq.map((f) => (
            <div key={f.q} className="pb-8 border-b border-border/50 last:border-0">
              <h3 className="text-lg font-bold mb-2">{f.q}</h3>
              <p className="text-muted-foreground leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
        <div className="mt-14 flex flex-wrap gap-4 text-sm">
          <Link to="/guest" className="text-gold hover:underline">Apply to be a guest</Link>
          <Link to="/about" className="text-gold hover:underline">About Raj Shamani</Link>
          <Link to="/raj-shamani-books" className="text-gold hover:underline">Raj Shamani books</Link>
          <Link to="/guides/how-to-start-a-podcast-india" className="text-gold hover:underline">
            How to start a podcast in India
          </Link>
        </div>
      </section>
    <AionCta />
    <RelatedKeywords currentPath="/raj-shamani-podcast" />
    <RelatedPages pages={["aion", "podcast", "guest", "about", "book", "faq", "contact"]} />
      </div>
  );
}
